import { investigationStatusValueToLabel } from '@/constants/statuses'
import { colors, sxPresets } from '@/theme'
import type { Investigation } from '@/types/investigation'
import { Box, Link, Paper, Tooltip, Typography } from '@mui/material'

interface InvestigationDetailsProps {
  investigation: Investigation
}

interface DetailFieldProps {
  label: string
  value?: string | number | null
  asUrl?: string
  truncate?: boolean
}

function DetailField({ label, value, asUrl, truncate }: DetailFieldProps) {
  const displayValue = value || '-'
  const truncateSx = truncate
    ? { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
    : undefined

  const content = asUrl ? (
    <Link
      href={asUrl}
      target='_blank'
      rel='noopener noreferrer'
      underline='hover'
      sx={{
        color: colors.brand.primary,
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {displayValue}
    </Link>
  ) : (
    <Typography variant='fieldValue' sx={truncateSx}>
      {displayValue}
    </Typography>
  )

  return (
    <Box sx={truncate ? { minWidth: 0, overflow: 'hidden' } : undefined}>
      <Typography variant='fieldLabel' sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {truncate && value ? (
        <Tooltip title={String(value)} placement='bottom-start'>
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </Box>
  )
}

export default function InvestigationDetails({ investigation }: InvestigationDetailsProps) {
  return (
    <Paper id='investigation-details' elevation={0} sx={{ border: `1px solid ${colors.border.default}` }}>
      <Box sx={sxPresets().section.header}>
        <Typography variant='sectionTitle'>Investigation details</Typography>
      </Box>

      <Box sx={{ ...sxPresets().section.content, ...sxPresets().grid.fourColumn }}>
        <DetailField label='Investigation name' value={investigation.name} />
        <DetailField
          label='Order'
          value={investigation.orderNumber}
          asUrl={investigation.orderId ? `/orders/${investigation.orderId}` : undefined}
        />
        <DetailField
          label='Product'
          value={investigation.product}
          asUrl={investigation.productVariationHash ? `/products/${investigation.productVariationHash}` : undefined}
        />
        <DetailField
          label='Patient'
          value={investigation.patientName}
          asUrl={investigation.patientId ? `/patients/${investigation.patientId}` : undefined}
        />
        <DetailField label='Result' value={investigation.result} />
        <DetailField label='Result raw' value={investigation.resultRaw} truncate />
        <DetailField label='Stage' value={investigationStatusValueToLabel(investigation.status)} />
        <DetailField label='External ID' value={investigation.externalId} />
      </Box>
    </Paper>
  )
}
